import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getPaddle } from "@/lib/paddle";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Zap, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PENDING_KEY = "contentos_pending_purchase";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [paddleReady, setPaddleReady] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  useEffect(() => {
    api.get("/api/billing/plans").then((r) => setPlans(r.data));
    getPaddle().then((p) => setPaddleReady(!!p));
  }, []);

  const buy = async (tier) => {
    setBusy(tier);
    try {
      const paddle = await getPaddle();
      if (!paddle) throw new Error("Paddle checkout unavailable");

      // Anonymous purchase — backend generates a purchase_id we can use later at register
      const { data } = await api.post("/api/billing/checkout/anonymous", { tier });
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        purchase_id: data.purchase_id, tier, price_usd: plans.find((p) => p.tier === tier)?.price_usd,
      }));

      paddle.Checkout.open({
        items: [{ priceId: data.price_id, quantity: 1 }],
        customData: data.passthrough,
        settings: {
          displayMode: "overlay",
          theme: "dark",
          successUrl: `${window.location.origin}/create-profile?purchase=${data.purchase_id}&tier=${tier}`,
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Checkout failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border backdrop-blur-xl bg-background/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-back-home">
            <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
              <Zap className="h-4 w-4" strokeWidth={2.5}/>
            </div>
            <div className="font-display font-black tracking-tight text-xl">ContentOS</div>
          </Link>
          {user ? (
            <Button variant="outline" onClick={() => nav("/app")} data-testid="pricing-goto-app">Open app <ArrowRight className="h-4 w-4"/></Button>
          ) : (
            <Link to="/auth" data-testid="pricing-signin" className="text-sm font-medium text-muted-foreground hover:text-foreground">Already have an account? Sign in</Link>
          )}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-10 text-center relative">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none -z-10"/>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Pricing · Pick a plan</div>
        <h1 className="mt-4 font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[0.9]">
          Marketing Team: <span className="line-through text-muted-foreground">$11,500/mo</span>.<br/>
          ContentOS: <span className="text-primary">$79/mo.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
          Same six roles: content strategist, copywriter, social media manager, creative strategist, performance analyst, nurture architect. One monthly bill.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-4">
        {plans.map((t) => (
          <div
            key={t.tier}
            data-testid={`pricing-tier-${t.tier}`}
            className={`p-8 rounded border ${t.popular ? "border-primary bg-card" : "border-border bg-card"} relative`}
          >
            {t.popular && (
              <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded">Most popular</div>
            )}
            <div className="font-display font-bold text-2xl tracking-tight">{t.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display font-black text-5xl">${t.price_usd}</span>
              <span className="text-muted-foreground">/mo</span>
            </div>
            <ul className="mt-6 space-y-3">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={`pricing-buy-${t.tier}`}
              onClick={() => buy(t.tier)}
              disabled={!t.configured || !paddleReady || busy === t.tier}
              className="mt-8 w-full h-11"
            >
              {busy === t.tier ? <><Loader2 className="h-4 w-4 animate-spin"/> Opening checkout…</>
                : !t.configured ? "Not configured"
                : <>Choose {t.name} <ArrowRight className="h-4 w-4"/></>}
            </Button>
          </div>
        ))}
      </section>

      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="border border-border rounded p-6 bg-secondary/40 text-center">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">What happens next</div>
          <div className="mt-3 text-sm leading-relaxed">
            Pay → create your profile → 30–60 min onboarding so we learn your business → your ContentOS dashboard opens with every tool unlocked.
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            By subscribing you agree to the{" "}
            <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link>,{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>, and{" "}
            <Link to="/refunds" className="underline underline-offset-2 hover:text-foreground">Refund Policy</Link>.
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5"/> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
